import OpenAI from 'openai'
import { debug, debugGroup } from '../../debug'
import { ProcessingOptions } from '../types'
import { processSurveyResults } from '../../survey/process-results'
import { hasResponse, hasStack } from '../../type-fixes'

export class OpenAIProvider {
  private client: OpenAI
  private config: {
    model: string
    temperature: number
    maxTokens: number
    assistantId?: string
    maxAttempts: number
    pollInterval: number
    timeout: number
  }

  constructor(apiKey: string, config: Record<string, any> = {}) {
    this.client = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true
    })

    this.config = {
      model: config.model || 'gpt-4',
      temperature: config.temperature || 0.3,
      maxTokens: config.maxTokens || 6000,
      assistantId: config.assistantId,
      maxAttempts: config.maxAttempts || 120,
      pollInterval: config.pollInterval || 2000,
      timeout: config.timeout || 180000
    }

    debugGroup('AI_PROVIDER', 'OpenAI Provider Initialized', () => {
      console.log('Configuration:', {
        model: this.config.model,
        temperature: this.config.temperature,
        maxTokens: this.config.maxTokens,
        hasAssistantId: !!this.config.assistantId,
        maxAttempts: this.config.maxAttempts,
        pollInterval: this.config.pollInterval,
        timeout: this.config.timeout
      })
    })
  }

  async processSurveyResults(surveyCode: string, options: ProcessingOptions = {}) {
    debugGroup('AI_PROVIDER', 'Starting Survey Processing', () => {
      console.log('Survey Code:', surveyCode)
      console.log('Processing Options:', options)
      console.log('Provider Config:', this.config)
    })

    try {
      // Get processed survey data
      const surveyData = await processSurveyResults(surveyCode)
      
      debugGroup('AI_PROVIDER', 'Survey Data Processed', () => {
        console.log('Total Roles:', surveyData.survey_results.roles.length)
        console.log('Has Averages:', !!surveyData.survey_results.averages)
        console.log('Role Types:', surveyData.survey_results.roles.map(r => r.role))
        console.log('Full Survey Data:', JSON.stringify(surveyData, null, 2))
      })

      // Prepare request
      const request = {
        model: options.model || this.config.model,
        temperature: options.temperature || this.config.temperature,
        maxTokens: this.config.maxTokens,
        assistantId: this.config.assistantId,
        data: surveyData
      }

      debugGroup('AI_PROVIDER', 'Request Configuration', () => {
        console.log('Model:', request.model)
        console.log('Temperature:', request.temperature)
        console.log('Max Tokens:', request.maxTokens)
        console.log('Assistant ID:', request.assistantId)
      })

      let response: string

      if (this.config.assistantId) {
        debug('AI_PROVIDER', 'Using Assistant API with ID:', this.config.assistantId)

        // Create thread
        const thread = await this.client.beta.threads.create()
        debug('AI_PROVIDER', 'Created thread:', thread.id)

        // Add message
        const message = await this.client.beta.threads.messages.create(thread.id, {
          role: 'user',
          content: JSON.stringify(surveyData, null, 2)
        })
        debug('AI_PROVIDER', 'Added message to thread:', message.id)

        // Run assistant
        const run = await this.client.beta.threads.runs.create(thread.id, {
          assistant_id: this.config.assistantId
        })
        debug('AI_PROVIDER', 'Started assistant run:', run.id)

        // Wait for completion
        const completedRun = await this.waitForRunCompletion(thread.id, run.id)
        debug('AI_PROVIDER', 'Run completed:', {
          status: completedRun.status,
          completedAt: completedRun.completed_at,
          usage: completedRun.usage
        })

        // Get messages
        const messages = await this.client.beta.threads.messages.list(thread.id)
        debug('AI_PROVIDER', 'Retrieved messages:', {
          count: messages.data.length,
          firstMessageId: messages.data[0]?.id
        })

        const lastMessage = messages.data[0]

        if (lastMessage?.content[0]?.type === 'text') {
          const content = lastMessage.content[0].text.value
          debugGroup('AI_PROVIDER', 'Processing Response', () => {
            console.log('Raw Response Length:', content.length)
            console.log('Response Preview:', content.substring(0, 200))
          })

          // Parse and clean the response
          try {
            // First try to extract JSON from markdown code blocks
            const jsonMatch = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/)
            if (jsonMatch) {
              debug('AI_PROVIDER', 'Found JSON code block, parsing...')
              const parsedJson = JSON.parse(jsonMatch[1])
              response = JSON.stringify(parsedJson)
            } else {
              // Try parsing the content directly
              debug('AI_PROVIDER', 'No JSON code block found, attempting direct parse...')
              const parsedJson = JSON.parse(content)
              response = JSON.stringify(parsedJson)
            }
          } catch (parseError) {
            debug('AI_PROVIDER', 'JSON parsing failed:', parseError)
            // If parsing fails, clean and store the raw content
            response = content
              .replace(/\\n/g, ' ')  // Replace escaped newlines with spaces
              .replace(/\s+/g, ' ')  // Normalize whitespace
              .trim()
          }

          debugGroup('AI_PROVIDER', 'Response Processing Complete', () => {
            console.log('Final Response Length:', response.length)
            console.log('Is Valid JSON:', (() => {
              try {
                JSON.parse(response)
                return true
              } catch {
                return false
              }
            })())
          })
        } else {
          throw new Error('Invalid response format from Assistant API')
        }
      } else {
        debug('AI_PROVIDER', 'Using Chat Completion API')

        const completion = await this.client.chat.completions.create({
          model: request.model,
          temperature: request.temperature,
          messages: [
            {
              role: 'system',
              content: 'You are an expert at analyzing organizational survey data and providing strategic insights. Always respond with valid JSON.'
            },
            {
              role: 'user',
              content: JSON.stringify(request.data, null, 2)
            }
          ]
        })

        debugGroup('AI_PROVIDER', 'Chat Completion Response', () => {
          console.log('Response:', completion.choices[0]?.message)
          console.log('Usage:', completion.usage)
        })

        const content = completion.choices[0]?.message?.content || ''
        
        try {
          const parsedJson = JSON.parse(content)
          response = JSON.stringify(parsedJson)
        } catch (parseError) {
          debug('AI_PROVIDER', 'JSON parse failed, cleaning content')
          response = content
            .replace(/\\n/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
        }
      }

      debug('AI_PROVIDER', 'Successfully generated insights')
      return response

    } catch (error) {
      debugGroup('AI_PROVIDER', 'Error in processSurveyResults', () => {
        console.error('Error:', error)
        console.error('Stack:', hasStack(error) ? error.stack : 'No stack available')
        if (hasResponse(error)) {
          console.error('OpenAI Response:', error.response)
        }
      })
      throw error
    }
  }

  private async waitForRunCompletion(threadId: string, runId: string) {
    let run = await this.client.beta.threads.runs.retrieve(threadId, runId)
    let attempts = 0
    const startTime = Date.now()
    
    while (run.status === 'queued' || run.status === 'in_progress') {
      debug('AI_PROVIDER', 'Run status:', {
        status: run.status,
        attempt: attempts + 1,
        threadId,
        runId,
        elapsedTime: `${Math.round((Date.now() - startTime) / 1000)}s`
      })

      // Check for timeout
      if (Date.now() - startTime > this.config.timeout) {
        debugGroup('AI_PROVIDER', 'Run Timeout', () => {
          console.error('Thread ID:', threadId)
          console.error('Run ID:', runId)
          console.error('Elapsed Time:', `${Math.round((Date.now() - startTime) / 1000)}s`)
          console.error('Max Time:', `${Math.round(this.config.timeout / 1000)}s`)
        })
        throw new Error(`Run timed out after ${Math.round(this.config.timeout / 1000)} seconds`)
      }

      // Wait for poll interval
      await new Promise(resolve => setTimeout(resolve, this.config.pollInterval))
      
      try {
        run = await this.client.beta.threads.runs.retrieve(threadId, runId)
      } catch (error) {
        debugGroup('AI_PROVIDER', 'Error retrieving run status', () => {
          console.error('Error:', error)
          console.error('Thread ID:', threadId)
          console.error('Run ID:', runId)
          console.error('Attempt:', attempts + 1)
        })
        throw error
      }
      
      attempts++
      if (attempts >= this.config.maxAttempts) {
        debugGroup('AI_PROVIDER', 'Max Attempts Exceeded', () => {
          console.error('Thread ID:', threadId)
          console.error('Run ID:', runId)
          console.error('Attempts:', attempts)
          console.error('Max Attempts:', this.config.maxAttempts)
        })
        throw new Error(`Run exceeded maximum attempts (${this.config.maxAttempts})`)
      }
    }

    if (run.status === 'failed') {
      debugGroup('AI_PROVIDER', 'Run Failed', () => {
        console.error('Error:', run.last_error)
        console.error('Thread ID:', threadId)
        console.error('Run ID:', runId)
        console.error('Elapsed Time:', `${Math.round((Date.now() - startTime) / 1000)}s`)
      })
      throw new Error('Assistant run failed: ' + run.last_error?.message)
    }

    if (run.status === 'cancelled' || run.status === 'expired' || run.status === 'cancelling') {
      debugGroup('AI_PROVIDER', 'Run Terminated', () => {
        console.error('Status:', run.status)
        console.error('Thread ID:', threadId)
        console.error('Run ID:', runId)
        console.error('Elapsed Time:', `${Math.round((Date.now() - startTime) / 1000)}s`)
      })
      throw new Error(`Run ${run.status}`)
    }

    debug('AI_PROVIDER', 'Run completed successfully:', {
      threadId,
      runId,
      status: run.status,
      completedAt: run.completed_at,
      elapsedTime: `${Math.round((Date.now() - startTime) / 1000)}s`
    })

    return run
  }
}